import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { SiteHeader } from '@/features/landing/components/SiteHeader';
import { SiteFooter } from '@/features/landing/components/SiteFooter';
import { Landing } from '@/features/landing/components/Landing';
import { ScrollToTopButton } from '@/features/landing/components/ScrollToTopButton';

/**
 * LandingPage — public Workerize landing (design contract:
 * docs/design/Stitch_Exact_Theme_Extraction.md, DEC-051).
 *
 * A single token-driven composition serves both themes — light and dark are
 * handled entirely by the CSS-variable system on `[data-theme]`, not by
 * separate per-theme component trees. Workerize logo (Brand System V1), PRD/i18n
 * copy (EN/AR), and the approved Electric-Lime tokens are baked in. The header
 * and footer are part of the landing (this route is not wrapped in
 * PublicLayout).
 */
export default function LandingPage(): ReactNode {
  const location = useLocation();

  // Scroll to the section named in the URL hash (e.g. arriving at "/#features"
  // from another page via the header nav). Retries on the next frame because
  // sections below the fold may not be laid out on the first paint.
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.slice(1);
    const scroll = (): boolean => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return true;
      }
      return false;
    };
    if (scroll()) return undefined;
    const raf = requestAnimationFrame(() => scroll());
    return () => cancelAnimationFrame(raf);
  }, [location.hash, location.key]);

  return (
    <div className="overflow-x-hidden bg-surface-base">
      <SiteHeader />
      <Landing />
      <SiteFooter year={new Date().getFullYear()} />
      <ScrollToTopButton />
    </div>
  );
}
